import * as React from "react";
import { Route, Navigate, RouterProvider, createBrowserRouter, createRoutesFromElements } from "react-router-dom";
import { HomeRoute } from "./pages/home";
import Task from "./pages/task";
import { Usage } from "./pages/usage-ng";
import OrgRoute from "./pages/org";
import GVCRoute from "./pages/gvc";
import WorkloadRoute from "./pages/workload";
import VolumeSetRoute from "./pages/volumeset";
import IdentityRoute from "./pages/identity";
import LocationRoute from "./pages/location";
import Mk8sRoute from "./pages/mk8s";
import AgentRoute from "./pages/agent";
import SecretRoute from "./pages/secret";
import QuotaRoute from "./pages/quota";
import CloudAccountRoute from "./pages/cloudaccount";
import DomainRoute from "./pages/domain";
import IpSetRoute from "./pages/ipset";
import RepositoryRoute from "./pages/repository";
import ImageRoute from "./pages/image";
import { AuditTrail } from "./pages/auditTrail";
import { LogsRoute } from "./pages/logs/index";
import AuditContextRoute from "./pages/auditContext";
import UserRoute from "./pages/user";
import PolicyRoute from "./pages/policy";
import ServiceaccountRoute from "./pages/serviceaccount";
import { EducationalVideosRoute } from "./pages/educationalVideos";
import GroupRoute from "./pages/group";
import HelmReleasesRoute from "./pages/helmReleases";
import JobExecutionsRoute from "./pages/jobExecutions";
import MarketplaceRoute from "./pages/marketplace/route";
import { emitterGVC, emitterGVCContext } from "./emitters/gvc";
import { PrivateRoute } from "./privateRoute";
import { Modal, notification } from "antd";
import { ConsoleRoute } from "./pages/console";
import { NMainHeightContext, useInitNMainHeight } from "./reactContexts/nMainHeightContext";
import { GlobalElement } from "./globalElement";
import { Redirect } from "./redirect";
import { doPeriodicTokenExpirationCheck, getK8sCrdSupportedKinds, getTerraformSupportedKinds } from "./services/cpln";
import { CPLNApply } from "./pages/cplnapply";
import { PrivateRouteDelayed } from "./privateRouteDelayed";
import { TriggerSentry } from "./pages/triggerSentry";
import Credits from "./pages/credits";
import { IS_CYPRESS, TOKEN_EXP_CHECK_INTERVAL_MS } from "./envVariables";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { initializeTheme } from "./store/themeSlice";
import { Health } from "./pages/health/health";
import { selectHasOrg, selectHasOrgInvite } from "./store/userDataSlice";
import { selectInitialUrl, setAlertContent } from "./store/uiDataSlice";
import { store } from "./store";
import { clearOverride, setOverride } from "./store/timezoneSlice";
import { selectContext } from "./store/contextSlice";
import { BillingRoute } from "./pages/billing";
import { SpectrumProvider } from "./newcomponents/spectrum";
import { Metrics } from "./pages/metrics/metrics";
import { Layout } from "./layout";

const HandleRootRedirect: React.FC = () => {
  const hasOrg = useAppSelector(selectHasOrg);
  const hasOrgInvite = useAppSelector(selectHasOrgInvite);
  const initialUrl = useAppSelector(selectInitialUrl);

  console.log("root redirect", window.location.pathname);

  if (initialUrl.includes("/task/")) {
    console.log("root redirect, has task, to initial", initialUrl);
    return <Navigate to={initialUrl} />;
  }

  if (!hasOrg && hasOrgInvite) {
    console.log("no org item, has invite, to task");
    return <Navigate to={`/task`} />;
  }

  console.log("to console");
  return <Navigate to={"/console"} />;
};

export const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const themeIsRespectingOS = useAppSelector((state) => state.theme.isRespectingOS);

  const nMainHeightHook = useInitNMainHeight();
  const [isConnectionLost, setIsConnectionLost] = React.useState(false);

  React.useEffect(() => {
    notification.config({
      placement: "bottomRight",
    });
  }, []);

  React.useEffect(() => {
    function changeHandler() {
      setIsConnectionLost(!navigator.onLine);
    }
    // @ts-ignore
    if (navigator.connection) {
      // @ts-ignore
      navigator.connection.onchange = changeHandler;
    }
    return () => {
      // @ts-ignore
      if (navigator.connection) {
        // @ts-ignore
        navigator.connection.onchange = null;
      }
    };
  }, []);

  React.useEffect(() => {
    if (!themeIsRespectingOS) {
      return;
    }
    const onChange = () => {
      dispatch(initializeTheme());
    };
    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", onChange);
    return () => {
      window.matchMedia("(prefers-color-scheme: dark)").removeEventListener("change", onChange);
    };
  }, [themeIsRespectingOS]);

  const context = useAppSelector(selectContext);

  const timeoutSeconds = useAppSelector((state) => state.context.timeoutSeconds);
  React.useEffect(() => {
    const id = setInterval(() => {
      doPeriodicTokenExpirationCheck(timeoutSeconds);
    }, TOKEN_EXP_CHECK_INTERVAL_MS);
    return () => {
      if (id) {
        clearTimeout(id);
      }
    };
  }, [context.org, timeoutSeconds]);

  React.useEffect(() => {
    if (IS_CYPRESS) {
      return;
    }
    const orgs = store.getState().userData.orgs;
    const orgItem = orgs.find((orgItem) => orgItem.name === context.org);
    if (
      orgItem &&
      (!orgItem.spec?.observability?.defaultAlertEmails || orgItem.spec.observability.defaultAlertEmails.length < 1)
    ) {
      dispatch(
        setAlertContent({
          id: `defaultAlertEmail-${context.org}`,
          title: `It is highly recommended to configure the Default Alert Email.`,
          message: ` to view the observability settings for org ${context.org}.`,
          link: `/console/org/${context.org}/info/observability`,
        }),
      );
    } else {
      dispatch(setAlertContent(null));
    }
  }, [context.org]);

  React.useEffect(() => {
    getTerraformSupportedKinds();
    getK8sCrdSupportedKinds();
  }, []);

  // Memoize the router to prevent recreation on every render.
  // Recreating the router causes navigation issues, especially when
  // navigating from home page after selecting an org.
  const router = React.useMemo(() => createBrowserRouter(
    createRoutesFromElements(
      <Route element={<GlobalElement />}>
        <Route path="/" element={<HandleRootRedirect />} />
        <Route
          path={"/task/*"}
          element={
            <PrivateRoute>
              <Task />
            </PrivateRoute>
          }
        />
        <Route path={`/billing/*`} element={<BillingRoute />} />
        <Route
          path={"/home/*"}
          element={
            <PrivateRoute>
              <HomeRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console"
          element={
            <PrivateRoute>
              <ConsoleRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/apply"
          element={
            <PrivateRoute>
              <CPLNApply />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/health/*"
          element={
            <PrivateRoute>
              <Health />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/workload/*"
          element={
            <PrivateRoute>
              <WorkloadRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/identity/*"
          element={
            <PrivateRoute>
              <IdentityRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/volumeset/*"
          element={
            <PrivateRoute>
              <VolumeSetRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/gvc/*"
          element={
            <PrivateRoute>
              <GVCRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/usage"
          element={
            <PrivateRouteDelayed
              onMount={() => dispatch(setOverride("Etc/UTC"))}
              onClear={() => dispatch(clearOverride())}
            >
              <Usage key={context.org} />
            </PrivateRouteDelayed>
          }
        />
        <Route
          path="/console/org/:org/logs"
          element={
            <PrivateRoute>
              <LogsRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/job-executions"
          element={
            <PrivateRoute>
              <JobExecutionsRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/location/*"
          element={
            <PrivateRoute>
              <LocationRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/mk8s/*"
          element={
            <PrivateRoute>
              <Mk8sRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/agent/*"
          element={
            <PrivateRoute>
              <AgentRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/secret/*"
          element={
            <PrivateRoute>
              <SecretRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/quota/*"
          element={
            <PrivateRoute>
              <QuotaRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/cloudaccount/*"
          element={
            <PrivateRoute>
              <CloudAccountRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/domain/*"
          element={
            <PrivateRoute>
              <DomainRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/ipset/*"
          element={
            <PrivateRoute>
              <IpSetRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/repository/*"
          element={
            <PrivateRoute>
              <RepositoryRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/image/*"
          element={
            <PrivateRoute>
              <ImageRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/audittrail"
          element={
            <PrivateRoute>
              <AuditTrail />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/metrics"
          element={
            <PrivateRoute>
              <Layout>
                <Metrics />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/auditctx/*"
          element={
            <PrivateRoute>
              <AuditContextRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/user/*"
          element={
            <PrivateRoute>
              <UserRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/policy/*"
          element={
            <PrivateRoute>
              <PolicyRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/serviceaccount/*"
          element={
            <PrivateRoute>
              <ServiceaccountRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/group/*"
          element={
            <PrivateRoute>
              <GroupRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/helmrelease/*"
          element={
            <PrivateRoute>
              <HelmReleasesRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/:org/marketplace/*"
          element={
            <PrivateRoute>
              <MarketplaceRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/org/*"
          element={
            <PrivateRoute>
              <OrgRoute />
            </PrivateRoute>
          }
        />
        <Route
          path="/console/educational-videos/*"
          element={
            <PrivateRoute>
              <EducationalVideosRoute />
            </PrivateRoute>
          }
        />
        <Route path="trigger-sentry" element={<TriggerSentry />} />
        <Route path={"redirect"} element={<Redirect />} />
        <Route path={"credits"} element={<Credits />} />
        <Route path={"*"} element={<Navigate to={"/"} />} />
      </Route>,
    ),
  ), []);

  return (
    <SpectrumProvider>
      <Modal
        open={isConnectionLost}
        forceRender={true}
        closable={false}
        destroyOnHidden={false}
        maskClosable={false}
        footer={null}
        title={"Connection is Lost"}
      >
        <span style={{ color: "var(--color-label-secondary)" }}>
          Your network connection is lost. Please resolve the issue to continue.
        </span>
      </Modal>
      {/* TODO remove */}
      <emitterGVCContext.Provider value={emitterGVC}>
        {/* TODO remove */}
        <NMainHeightContext.Provider value={nMainHeightHook}>
          <RouterProvider router={router} />
        </NMainHeightContext.Provider>
      </emitterGVCContext.Provider>
    </SpectrumProvider>
  );
};
